import { useEffect, useRef, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
    Plus,
    Loader2,
    Pencil,
    Trash2,
    Search,
    School,
    X,
    GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const ANY = "__any__";

export default function ManageColleges() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState(ANY);

    const [formOpen, setFormOpen] = useState(false);
    const [draft, setDraft] = useState({ id: null, name: "", status: "Active" });
    const [submitting, setSubmitting] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const dragIdxRef = useRef(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const canReorder = !search.trim() && status === ANY;

    const fetchAll = async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (status !== ANY) params.status = status;
            const { data } = await api.get("/colleges", { params });
            setRows(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const apply = () => {
        setLoading(true);
        fetchAll();
    };

    const openAdd = () => {
        setDraft({ id: null, name: "", status: "Active" });
        setFormOpen(true);
    };

    const openEdit = (c) => {
        setDraft({ id: c.id, name: c.name, status: c.status });
        setFormOpen(true);
    };

    const save = async () => {
        setSubmitting(true);
        try {
            if (draft.id) {
                await api.put(`/colleges/${draft.id}`, {
                    name: draft.name,
                    status: draft.status,
                });
                toast.success("College updated");
            } else {
                await api.post("/colleges", {
                    name: draft.name,
                    status: draft.status,
                });
                toast.success("College added");
            }
            setFormOpen(false);
            apply();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    const toggleStatus = async (c) => {
        const next = c.status === "Active" ? "Inactive" : "Active";
        try {
            await api.patch(`/colleges/${c.id}/status`, { status: next });
            toast.success(`Marked ${next}`);
            apply();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete(`/colleges/${deleteTarget.id}`);
            toast.success("College deleted");
            setDeleteTarget(null);
            apply();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setDeleting(false);
        }
    };

    const persistReorder = async (nextRows) => {
        try {
            await api.put("/colleges/reorder", {
                ids: nextRows.map((r) => r.id),
            });
        } catch (e) {
            toast.error(apiErrorMessage(e));
            apply();
        }
    };

    const handleDrop = (targetIdx) => {
        const from = dragIdxRef.current;
        dragIdxRef.current = null;
        setDragOverIdx(null);
        if (from === null || from === undefined || from === targetIdx) return;
        const next = [...rows];
        const [moved] = next.splice(from, 1);
        next.splice(targetIdx, 0, moved);
        setRows(next);
        persistReorder(next);
    };

    const pill = (s) =>
        s === "Active" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                Active
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#F7F5F0] text-[#0F1F38]/70 border border-[#0F1F38]/15">
                Inactive
            </span>
        );

    return (
        <div data-testid="manage-colleges-page">
            <AdminPageHeader
                eyebrow="College Master"
                title="Manage Colleges"
                description="Colleges shown in the Register Now dropdown come from this list. Inactive colleges are hidden from candidates but still preserved on existing records."
                actions={
                    <button
                        onClick={openAdd}
                        data-testid="btn-add-college"
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254]"
                    >
                        <Plus size={14} strokeWidth={1.5} />
                        Add College
                    </button>
                }
            />

            <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
                <div className="bg-white border border-[#0F1F38]/10 p-4 md:p-5 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Search
                        </Label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                            />
                            <Input
                                data-testid="filter-search"
                                className="pl-9"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && apply()}
                                placeholder="College name…"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Status
                        </Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="filter-status">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-end gap-2">
                        <button
                            data-testid="btn-reset"
                            onClick={() => {
                                setSearch("");
                                setStatus(ANY);
                                setTimeout(apply, 0);
                            }}
                            className="px-3 py-2 text-xs border border-[#0F1F38]/15"
                        >
                            <X size={12} className="inline mr-1" />
                            Reset
                        </button>
                        <button
                            data-testid="btn-apply"
                            onClick={apply}
                            className="flex-1 px-3 py-2 text-xs bg-[#0F1F38] text-[#F7F5F0]"
                        >
                            Apply
                        </button>
                    </div>
                </div>

                <div className="mt-4 bg-white border border-[#0F1F38]/10">
                    {canReorder && !loading && rows.length > 1 && (
                        <div className="px-4 py-2 text-[11px] text-[#0F1F38]/60 border-b border-[#0F1F38]/10 bg-[#F7F5F0]">
                            Drag rows by the <GripVertical size={11} className="inline -mt-0.5" /> handle to change the order candidates see in the Register Now dropdown.
                        </div>
                    )}
                    {!canReorder && !loading && rows.length > 0 && (
                        <div className="px-4 py-2 text-[11px] text-[#0F1F38]/60 border-b border-[#0F1F38]/10 bg-[#F7F5F0]">
                            Reset the filters above to enable drag-and-drop reordering.
                        </div>
                    )}
                    {loading ? (
                        <div className="p-16 text-center text-[#4B5563]">
                            <Loader2 size={18} className="animate-spin inline mr-2" />
                            Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-16 text-center text-[#4B5563]">
                            <School size={28} className="mx-auto mb-3 text-[#0F1F38]/30" />
                            <div className="font-medium text-[#0F1F38]">
                                No colleges yet
                            </div>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10"></TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Toggle</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((c, idx) => (
                                    <TableRow
                                        key={c.id}
                                        data-testid={`college-row-${c.id}`}
                                        draggable={canReorder}
                                        onDragStart={(e) => {
                                            if (!canReorder) return;
                                            dragIdxRef.current = idx;
                                            e.dataTransfer.effectAllowed = "move";
                                        }}
                                        onDragOver={(e) => {
                                            if (!canReorder) return;
                                            e.preventDefault();
                                            if (dragOverIdx !== idx) setDragOverIdx(idx);
                                        }}
                                        onDragLeave={() => {
                                            if (dragOverIdx === idx) setDragOverIdx(null);
                                        }}
                                        onDrop={(e) => {
                                            if (!canReorder) return;
                                            e.preventDefault();
                                            handleDrop(idx);
                                        }}
                                        className={
                                            canReorder
                                                ? dragOverIdx === idx
                                                    ? "bg-[#0F1F38]/5"
                                                    : ""
                                                : ""
                                        }
                                    >
                                        <TableCell
                                            className={
                                                canReorder
                                                    ? "text-[#0F1F38]/40 cursor-grab active:cursor-grabbing"
                                                    : "text-[#0F1F38]/15"
                                            }
                                            title={
                                                canReorder
                                                    ? "Drag to reorder"
                                                    : "Clear filters to reorder"
                                            }
                                            data-testid={`drag-handle-${c.id}`}
                                        >
                                            <GripVertical size={14} />
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {c.name}
                                        </TableCell>
                                        <TableCell>{pill(c.status)}</TableCell>
                                        <TableCell>
                                            <Switch
                                                data-testid={`toggle-${c.id}`}
                                                checked={c.status === "Active"}
                                                onCheckedChange={() => toggleStatus(c)}
                                            />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <button
                                                data-testid={`btn-edit-${c.id}`}
                                                onClick={() => openEdit(c)}
                                                className="p-1.5 hover:bg-[#0F1F38]/5"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                data-testid={`btn-delete-${c.id}`}
                                                onClick={() => setDeleteTarget(c)}
                                                className="p-1.5 hover:bg-red-50 text-red-600"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>

            {/* Add / Edit dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent data-testid="college-form-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {draft.id ? "Edit College" : "Add College"}
                        </DialogTitle>
                    </DialogHeader>
                    <fieldset disabled={submitting} className="space-y-4 py-2">
                        <div>
                            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                Name *
                            </Label>
                            <Input
                                data-testid="draft-name"
                                value={draft.name}
                                onChange={(e) =>
                                    setDraft({ ...draft, name: e.target.value })
                                }
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                                Status
                            </Label>
                            <Select
                                value={draft.status}
                                onValueChange={(v) => setDraft({ ...draft, status: v })}
                            >
                                <SelectTrigger data-testid="draft-status">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Active">Active</SelectItem>
                                    <SelectItem value="Inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </fieldset>
                    <DialogFooter>
                        <button
                            onClick={() => setFormOpen(false)}
                            disabled={submitting}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-save-college"
                            onClick={save}
                            disabled={submitting || !draft.name.trim()}
                            className="px-4 py-2 text-sm bg-[#0F1F38] text-[#F7F5F0] disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {submitting && <Loader2 size={14} className="animate-spin" />}
                            {draft.id ? "Update" : "Add"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(v) => !v && setDeleteTarget(null)}
            >
                <DialogContent data-testid="college-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete {deleteTarget?.name}?</DialogTitle>
                        <DialogDescription>
                            Existing candidates using this college keep their record.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteTarget(null)}
                            disabled={deleting}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-delete-college"
                            onClick={confirmDelete}
                            disabled={deleting}
                            className="px-4 py-2 text-sm bg-red-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {deleting && <Loader2 size={14} className="animate-spin" />}
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
